import {
	InvoiceRecipientType,
	type OrganizationInvoiceRecipientInput,
	useGetAllOrganizationUserQuery,
	useGetOrganizationSettingQuery,
	useSetOrganizationInvoiceRecipientsMutation,
} from '@codezo/graphql';
import { ChevronDown, Mail, Plus, User, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Field } from '../../components/ui/field';
import { Input } from '../../components/ui/input';
import { SelectPopover } from '../../components/ui/select-popover';
import { SettingsCard } from '../../components/ui/settings-card';
import { useAuth } from '../../hooks/useAuth';

interface LocalRecipient {
	key: string;
	type: InvoiceRecipientType;
	userId?: string;
	email?: string;
	name?: string;
}

const EMAIL_RE = /^\S+@\S+\.\S+$/;

let keyCounter = 0;
const nextKey = () => `r_${keyCounter++}`;

const toInput = (r: LocalRecipient): OrganizationInvoiceRecipientInput =>
	r.type === InvoiceRecipientType.User
		? { type: InvoiceRecipientType.User, userId: r.userId, name: r.name }
		: { type: InvoiceRecipientType.Email, email: r.email, name: r.name };

export function InvoiceRecipientsCard() {
	const { user } = useAuth();
	const organizationId = user?.organizationId || '';

	const {
		data: settingData,
		loading: settingLoading,
		refetch: refetchSetting,
	} = useGetOrganizationSettingQuery({
		variables: { organizationId },
		skip: !organizationId,
		fetchPolicy: 'no-cache',
	});

	const { data: usersData } = useGetAllOrganizationUserQuery({
		variables: {
			input: { organizationId, pagination: { currentPage: 1, limit: 200 } },
		},
		skip: !organizationId,
		fetchPolicy: 'no-cache',
	});

	const orgUsers = useMemo(() => usersData?.getAllOrganizationUser?.data ?? [], [usersData]);

	const [saveRecipients, { loading: saving }] = useSetOrganizationInvoiceRecipientsMutation();

	const [recipients, setRecipients] = useState<LocalRecipient[]>([]);
	const [removingKey, setRemovingKey] = useState<string | null>(null);

	/* ── Add dialog ── */
	const [dialogMode, setDialogMode] = useState<InvoiceRecipientType | null>(null);
	const [pendingUserId, setPendingUserId] = useState('');
	const [emailInput, setEmailInput] = useState('');
	const [nameInput, setNameInput] = useState('');

	useEffect(() => {
		const list = settingData?.getOrganizationSetting?.invoiceRecipients ?? [];
		setRecipients(
			list.map((r) => ({
				key: nextKey(),
				type: r.type,
				userId: r.userId ?? undefined,
				email: r.email ?? undefined,
				name: r.name ?? undefined,
			}))
		);
	}, [settingData]);

	const userLabel = (userId?: string, fallback?: string) => {
		const u = orgUsers.find((ou) => ou.id === userId);
		if (u) {
			const name = `${u.firstName} ${u.lastName}`.trim();
			return {
				name: name || u.authenticationUser?.email || 'User',
				email: u.authenticationUser?.email,
			};
		}
		return { name: fallback || 'User', email: undefined };
	};

	const availableUsers = useMemo(
		() =>
			orgUsers.filter(
				(u) =>
					!recipients.some(
						(r) => r.type === InvoiceRecipientType.User && r.userId === u.id
					)
			),
		[orgUsers, recipients]
	);

	const userOptions = availableUsers.map((u) => ({
		value: u.id,
		label: `${u.firstName} ${u.lastName}`.trim() || u.authenticationUser?.email || 'User',
	}));

	/* ── Email field validation ── */
	const emailTrimmed = emailInput.trim();
	const emailDuplicate = recipients.some(
		(r) =>
			r.type === InvoiceRecipientType.Email &&
			r.email?.toLowerCase() === emailTrimmed.toLowerCase()
	);
	const emailError =
		emailTrimmed === ''
			? null
			: !EMAIL_RE.test(emailTrimmed)
				? 'Enter a valid email address'
				: emailDuplicate
					? 'That email is already added'
					: null;
	const canAddEmail = EMAIL_RE.test(emailTrimmed) && !emailDuplicate;

	/* ── Persist the whole list ── */
	const persist = async (next: LocalRecipient[]): Promise<boolean> => {
		try {
			const result = await saveRecipients({
				variables: { input: { organizationId, recipients: next.map(toInput) } },
			});
			if (result.data?.setOrganizationInvoiceRecipients.success) {
				setRecipients(next);
				refetchSetting();
				return true;
			}
			toast.error(
				result.data?.setOrganizationInvoiceRecipients.message || 'Failed to update'
			);
			return false;
		} catch {
			toast.error('Failed to update invoice recipients');
			return false;
		}
	};

	const openUserDialog = () => {
		setPendingUserId('');
		setDialogMode(InvoiceRecipientType.User);
	};

	const openEmailDialog = () => {
		setEmailInput('');
		setNameInput('');
		setDialogMode(InvoiceRecipientType.Email);
	};

	const confirmAddUser = async () => {
		const u = orgUsers.find((ou) => ou.id === pendingUserId);
		if (!u) return;
		const name = `${u.firstName} ${u.lastName}`.trim() || undefined;
		const next: LocalRecipient[] = [
			...recipients,
			{ key: nextKey(), type: InvoiceRecipientType.User, userId: u.id, name },
		];
		if (await persist(next)) {
			toast.success('Recipient added');
			setDialogMode(null);
		}
	};

	const confirmAddEmail = async () => {
		if (!canAddEmail) return;
		const next: LocalRecipient[] = [
			...recipients,
			{
				key: nextKey(),
				type: InvoiceRecipientType.Email,
				email: emailTrimmed,
				name: nameInput.trim() || undefined,
			},
		];
		if (await persist(next)) {
			toast.success('Recipient added');
			setDialogMode(null);
		}
	};

	const handleRemove = async (key: string) => {
		setRemovingKey(key);
		const ok = await persist(recipients.filter((r) => r.key !== key));
		if (ok) toast.success('Recipient removed');
		setRemovingKey(null);
	};

	if (settingLoading) {
		return (
			<div className="h-[200px] animate-pulse rounded-[var(--r-3)] border border-line-1 bg-surface" />
		);
	}

	return (
		<SettingsCard
			title="Invoice recipients"
			description="Who receives your organization's invoices — internal users and/or external emails."
			headerAction={
				<DropdownMenu>
					<DropdownMenuTrigger className="flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border border-line-1 bg-surface-sunken px-3 text-sm font-medium text-text-1 transition-colors hover:bg-surface-hover aria-expanded:bg-surface-fill">
						<Plus size={14} />
						Add recipient
						<ChevronDown size={12} className="text-text-4" />
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem
							onClick={openUserDialog}
							disabled={userOptions.length === 0}
						>
							<User size={14} /> System user
						</DropdownMenuItem>
						<DropdownMenuItem onClick={openEmailDialog}>
							<Mail size={14} /> External email
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			}
		>
			{/* ── Current recipients ── */}
			{recipients.length === 0 ? (
				<p className="py-1 text-sm text-text-4">No recipients yet.</p>
			) : (
				<ul className="flex flex-col divide-y divide-line-1">
					{recipients.map((r) => {
						const isUser = r.type === InvoiceRecipientType.User;
						const resolved = isUser
							? userLabel(r.userId, r.name)
							: { name: r.name, email: r.email };
						const primary = isUser ? resolved.name : resolved.name || r.email;
						const secondary = isUser
							? resolved.email
							: resolved.name
								? r.email
								: undefined;
						return (
							<li
								key={r.key}
								className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0"
							>
								<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-fill text-text-3">
									{isUser ? <User size={15} /> : <Mail size={15} />}
								</div>
								<div className="min-w-0 flex-1">
									<div className="truncate text-sm font-medium text-text-1">
										{primary}
									</div>
									{secondary && (
										<div className="truncate text-xs text-text-4">
											{secondary}
										</div>
									)}
								</div>
								<span className="shrink-0 rounded-full border border-line-1 px-2 py-0.5 text-2xs font-medium uppercase tracking-[0.04em] text-text-4">
									{isUser ? 'User' : 'Email'}
								</span>
								<button
									type="button"
									onClick={() => handleRemove(r.key)}
									disabled={removingKey === r.key}
									aria-label="Remove recipient"
									className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-text-4 transition-colors hover:bg-surface-hover hover:text-text-1 disabled:opacity-50"
								>
									<X size={15} />
								</button>
							</li>
						);
					})}
				</ul>
			)}

			{/* ── Add system user dialog ── */}
			<Dialog
				open={dialogMode === InvoiceRecipientType.User}
				onOpenChange={(v) => !v && setDialogMode(null)}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Add system user</DialogTitle>
						<DialogDescription>
							Choose an internal user to receive this organization&rsquo;s invoices.
						</DialogDescription>
					</DialogHeader>
					<DialogBody>
						<Field label="User">
							<SelectPopover
								value={pendingUserId}
								onChange={setPendingUserId}
								options={userOptions}
								placeholder="Select a user"
							/>
						</Field>
					</DialogBody>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDialogMode(null)}>
							Cancel
						</Button>
						<Button onClick={confirmAddUser} disabled={!pendingUserId || saving}>
							{saving ? 'Adding…' : 'Add recipient'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* ── Add external email dialog ── */}
			<Dialog
				open={dialogMode === InvoiceRecipientType.Email}
				onOpenChange={(v) => !v && setDialogMode(null)}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Add external email</DialogTitle>
						<DialogDescription>
							Add an external email address to receive this organization&rsquo;s
							invoices.
						</DialogDescription>
					</DialogHeader>
					<DialogBody className="flex flex-col gap-4">
						<Field label="Email address" required error={emailError}>
							<Input
								type="email"
								value={emailInput}
								onChange={(e) => setEmailInput(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === 'Enter') confirmAddEmail();
								}}
								placeholder="ap@company.com"
								aria-invalid={!!emailError}
								autoFocus
							/>
						</Field>
						<Field label="Name (optional)">
							<Input
								value={nameInput}
								onChange={(e) => setNameInput(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === 'Enter') confirmAddEmail();
								}}
								placeholder="Accounts Payable"
							/>
						</Field>
					</DialogBody>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDialogMode(null)}>
							Cancel
						</Button>
						<Button onClick={confirmAddEmail} disabled={!canAddEmail || saving}>
							{saving ? 'Adding…' : 'Add recipient'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</SettingsCard>
	);
}
